import { AxiosPromise, AxiosRequestConfig } from 'axios'
import useAxios, { Options, RefetchOptions, ResponseValues } from 'axios-hooks'
import { useCallback, useMemo, useRef } from 'react'
import toast from 'react-hot-toast'
import { useLocation } from 'react-router-dom'

import { useAccountProviderCtx } from 'src/components/data/providers/AccountProvider/AccountProvider'
import useIsSnowflakeProduct from 'src/hooks/useIsSnowflakeProduct'
import { API_DEFAULT_CONFIG } from 'src/utils/constants/apiConfig'
import { LOGOUT_STATUS_ERRORS } from 'src/utils/constants/LogoutStatusErrors'
import storageUtils from 'src/utils/storageUtils'
import { LOGIN_ROOT, parse } from 'src/utils/urls'
import { waitForDelayFn } from 'src/utils/waitForDelayUtils'
import useDeepCompareMemo from '../useDeepCompareMemo'
import usePageLoading from '../usePageLoading'
import { getConfigWithPageHeaderIds, isErrorNeedRetryFn } from './useFetchLogic'

const MAX_RETRY = 3
const RETRY_DELAYS = [1000, 2000, 4000]

// This hook allow overriding useAxios with default settings and handling logout when necessary

const useFetch = <T>(
  config: AxiosRequestConfig | string,
  options?: Options,
  needAuth = true,
): [
  ResponseValues<T, any, any>,
  (config?: AxiosRequestConfig, options?: RefetchOptions, retryTime?: number) => AxiosPromise<T>,
  () => void, // Cancel request
] => {
  const { logout } = useAccountProviderCtx()
  const { pathname } = useLocation()
  const { updatePageLoadingAdomikNavigationId } = usePageLoading()
  const globalRequestIdRef = useRef<string | null>(null)

  const isSnowflakeProduct = useIsSnowflakeProduct()

  const currentRequestUrl = typeof config === 'string' ? config : config.url

  // Add special header in export mode
  const exportId = parse(window.location.search?.replace('?', ''))?.export_id as string | undefined

  const defaultConfig = useMemo(
    () => ({
      ...API_DEFAULT_CONFIG,
      headers: {
        ...API_DEFAULT_CONFIG.headers,
        ...(exportId ? { ['Adomik-Export-Id']: exportId } : {}),
      },
    }),
    [exportId],
  )

  const axiosDefaultConfigs = useDeepCompareMemo(
    () =>
      typeof config === 'string'
        ? {
            ...defaultConfig,
            url: config,
          }
        : {
            ...defaultConfig,
            ...config,
            ['headers']: { ...defaultConfig.headers, ...config.headers },
          },
    [config, defaultConfig],
  )

  // Original query
  const [responseValues, execute, cancelRequest] = useAxios(axiosDefaultConfigs, {
    ...(options ?? {}),
    autoCancel: false,
  })

  // react router context allow us to redirect the user to the login page
  const executeQuery = useCallback(
    async (config?: AxiosRequestConfig, options?: RefetchOptions, retryTime = 0) => {
      if (needAuth && !storageUtils.getAuthToken()) {
        logout && logout(false)
        // No token: don't call, never settle; the page is leaving for /login.
        return new Promise(() => {}) as AxiosPromise<any>
      }

      const axiosConfig = getConfigWithPageHeaderIds({
        currentConfig: config,
        requestUrl: currentRequestUrl,
        generateNewId: updatePageLoadingAdomikNavigationId,
        globalRequestIdRef,
        axiosDefaultConfigs,
      })

      try {
        return await execute(axiosConfig, options || {})
      } catch (error) {
        const err = error as any

        const isLogout =
          err.response &&
          LOGOUT_STATUS_ERRORS.includes(err.response.status) &&
          !pathname?.startsWith(LOGIN_ROOT)

        // if the request response includes LOGOUT_ERRORS status, logout user and redirect him to login page
        if (isLogout) {
          logout?.(false)
        }

        if (!isLogout && isErrorNeedRetryFn(err) && retryTime < MAX_RETRY) {
          await waitForDelayFn(RETRY_DELAYS[retryTime] || RETRY_DELAYS[0])
          return executeQuery(config, options, retryTime + 1)
        }

        if (err.message?.includes('timeout')) {
          toast.error('Request timed out')
          const url = config?.url || currentRequestUrl
          err.message = `${err.message} for ${url}`
        }

        throw error
      }
    },
    [
      axiosDefaultConfigs,
      currentRequestUrl,
      execute,
      pathname,
      logout,
      needAuth,
      updatePageLoadingAdomikNavigationId,
      isSnowflakeProduct,
    ],
  )

  const memoizedResponse = useMemo(
    () => [{ ...responseValues, data: responseValues.data }, executeQuery, cancelRequest],
    [cancelRequest, executeQuery, responseValues],
  )

  return memoizedResponse as [
    ResponseValues<T, any, any>,
    (config?: AxiosRequestConfig, options?: RefetchOptions, retryTime?: number) => AxiosPromise<T>,
    () => void,
  ]
}

export default useFetch
